// eslint-disable-next-line import/order -- must run first so jQuery is global before the jQuery plugins below load
import $ from '../Utilities/jquerySetup';
import * as widgets from 'surveyjs-widgets';
import * as SurveyCore from 'survey-core';
import 'nouislider/distribute/nouislider.min.css';
import 'jquery-ui-dist/jquery-ui.css';
// eslint-disable-next-line import/extensions
import 'velocity-animate/velocity.js';
// eslint-disable-next-line import/extensions
import 'jquery-ui-dist/jquery-ui.js';
import SurveyInaccessibleType, { InaccessibleTypeValue } from '../Data/SurveyInaccessibleType';
import SurveyType, { SurveyTypeValue } from '../Data/SurveyType';
import Languages from '../Data/languages';
import { applySurveyTextRules } from '../Utilities/StringReplaceHelper';
import type {
  Category,
  ClosedInformation,
  SurveyAnswer,
  SurveyJson,
  SurveyJsonElement,
  SurveyJsonPage,
  SurveyResponse,
  Template
} from '../types';

widgets.jqueryuidatepicker(SurveyCore);
widgets.nouislider(SurveyCore);

type RatingType = 'detractor' | 'passive' | 'promoter';

// type: detractor, passice, or promoter
const getVisibleOnRating = (ratingMaxValue: number, type: RatingType): string => {
  let passiveRatingTypeRange = { min: 7, max: 8 };

  if (ratingMaxValue === 5) {
    passiveRatingTypeRange = { min: 3, max: 3 };
  } else if (ratingMaxValue === 10) {
    passiveRatingTypeRange = { min: 7, max: 8 };
  }

  switch (type) {
    case 'detractor':
      return `{rating} < ${passiveRatingTypeRange.min}`;
    case 'passive':
      return `{rating} >= ${passiveRatingTypeRange.min} && {rating} <= ${passiveRatingTypeRange.max}`;
    case 'promoter':
      return `{rating} > ${passiveRatingTypeRange.max}`;
    default:
      return '';
  }
};

/**
 * From an HTML string, return a single line of text
 */
const getInnerTextFromHtmlString = (htmlString?: string): string | null => {
  if (!htmlString) {
    return htmlString ?? null;
  }
  const formattedHtmlString = htmlString
    // Replace <br> and <br/> tags with a whitespace
    .replace(/<br\s*\/?>/gi, ' ')
    // Replace </p> tags with a space only if followed by another HTML tag
    .replace(/<\/p>(?=<\w)/gi, ' ');

  // Parse the HTML string
  const parsedDom = new DOMParser().parseFromString(formattedHtmlString, 'text/html');

  // Check for parsing errors
  if (parsedDom.documentElement.querySelector('parsererror')) {
    return 'ERROR - The title format is invalid';
  }

  return parsedDom.documentElement.textContent;
};

const getLocaleFromCountryName = (countryName?: string): string => {
  switch (countryName) {
    case 'Dansk':
    case 'Danish':
      return 'da';
    case 'English':
      return 'en';
    case 'Norsk':
      return 'no';
    case 'Svenska':
      return 'sv';
    case 'Deutsch':
      return 'de';
    case 'русский':
      return 'ru';
    case 'Latviešu':
      return 'lv';
    case 'Lietuvos':
      return 'lt';
    case 'Eesti':
      return 'ee';
    case 'Føroyskt':
      return 'fo';
    case 'Kalaallisut':
      return 'kl';
    case 'Dansk-Kalaallisut':
      return 'kl/da';
    case 'Nederlands':
      return 'nl';
    case 'Italiano':
      return 'it';
    case 'Français':
      return 'fr';
    case 'Suomi':
      return 'fi';
    case 'Czech':
      return 'cs';
    case 'Spanish':
      return 'es';
    case 'Slovenian':
      return 'sl';
    case 'Polish':
      return 'pl';
    case 'Turkish':
      return 'tr';
    case 'Hungarian':
      return 'hu';
    case 'Portuguese':
      return 'pt';
    case 'Catalan':
      return 'ca';
    case 'Romanian':
      return 'ro';
    case 'Slovakian':
      return 'sk';
    case 'Chinese':
      return 'zh-cn';
    case 'Bulgarian':
      return 'bg';
    case 'Japanese':
      return 'ja';
    default:
      return '';
  }
};

function updateDisplayModeForRatings(elements?: SurveyJsonElement[]) {
  elements?.forEach((element) => {
    if (element.displayMode) return;
    if (element.type === 'rating') {
      element.displayMode = 'buttons';
    }

    if (element.elements && element.elements.length > 0) {
      updateDisplayModeForRatings(element.elements);
    }
  });
}

interface ModifiedSchemaArgs {
  schema: SurveyJson;
  template: Template;
}

const applyRatingElement = (ratingElement: SurveyJsonElement, template: Template): void => {
  ratingElement.isRequired = ratingElement.isRequired ?? true;
  ratingElement.displayMode = ratingElement.displayMode ?? 'buttons';
  if (!ratingElement.title) {
    ratingElement.title = getInnerTextFromHtmlString(template.content) ?? undefined;
  }
  if (!ratingElement.minRateDescription) {
    ratingElement.minRateDescription =
      getInnerTextFromHtmlString(template.lowRatingText) ?? undefined;
  }
  if (!ratingElement.maxRateDescription) {
    ratingElement.maxRateDescription =
      getInnerTextFromHtmlString(template.highRatingText) ?? undefined;
  }
};

const applyFollowUpComment = (
  element: SurveyJsonElement,
  ratingMaxValue: number,
  type: RatingType,
  fallbackTitle?: string
): void => {
  element.visible = element.visible ?? false;
  element.visibleIf = element.visibleIf ?? getVisibleOnRating(ratingMaxValue, type);
  if (element.title) {
    element.title = element.title.replace('{MAX_VALUE}', String(ratingMaxValue));
  } else {
    element.title = fallbackTitle;
  }
};

const applyCategoryElement = (categoryElement: SurveyJsonElement, template: Template): void => {
  // if no choices are defined in JSONSchema, use categories from template
  if (!categoryElement.choices) {
    const { categories } = template;
    if (categories?.length) {
      categoryElement.choices = categories.map((category) => {
        return {
          value: category.id,
          text: category.name ?? ''
        };
      });
    }
  }
  categoryElement.title = categoryElement.title ?? template.categoryQuestion;
  categoryElement.isRequired =
    categoryElement.isRequired ?? template.mandatoryCategoryQuestion ?? false;
  categoryElement.visibleIf = categoryElement.visibleIf ?? '{rating} notempty';
};

const applyOutro = (schema: SurveyJson, template: Template, ratingMaxValue: number): void => {
  const { outroText } = template;
  if (outroText) {
    schema.completedHtml = schema.completedHtml ?? outroText;
  }

  const { includeCustomOutroText } = template;
  if (includeCustomOutroText) {
    const detractorOutroText = template.outroDetractorText;
    const passiveOutroText = template.outroPassiveText;
    const promoterOutroText = template.outroPromoterText;
    schema.completedHtmlOnCondition = schema.completedHtmlOnCondition ?? [
      {
        expression: getVisibleOnRating(ratingMaxValue, 'detractor'),
        html: detractorOutroText ?? ''
      },
      {
        expression: getVisibleOnRating(ratingMaxValue, 'passive'),
        html: passiveOutroText ?? ''
      },
      {
        expression: getVisibleOnRating(ratingMaxValue, 'promoter'),
        html: promoterOutroText ?? ''
      }
    ];
  }
};

const applyPage = (page: SurveyJsonPage, schema: SurveyJson, template: Template): number => {
  let ratingMaxValue = 10;

  // SET STTYLING ON ALL RATING TYPE ELEMENTS
  updateDisplayModeForRatings(page.elements);

  // MAIN NPS RATING
  const ratingElement = page.elements?.find((element) => element.name === 'rating');
  if (ratingElement) {
    applyRatingElement(ratingElement, template);
  }

  // RATING COMMENTS: detractor, passive, and promoter
  const disappointingCommentElement = page.elements?.find(
    (element) => element.name === 'disappointing_experience'
  );
  const passiveCommentElement = page.elements?.find(
    (element) => element.name === 'passive_experience'
  );
  const satisfiedCommentElement = page.elements?.find(
    (element) => element.name === 'satisfied_experience'
  );
  if (ratingElement?.rateMax) {
    ratingMaxValue = ratingElement.rateMax;
  }

  if (disappointingCommentElement) {
    applyFollowUpComment(
      disappointingCommentElement,
      ratingMaxValue,
      'detractor',
      template.followUpDetractor
    );
  }
  if (passiveCommentElement) {
    applyFollowUpComment(
      passiveCommentElement,
      ratingMaxValue,
      'passive',
      template.followUpPassive
    );
  }
  if (satisfiedCommentElement) {
    applyFollowUpComment(
      satisfiedCommentElement,
      ratingMaxValue,
      'promoter',
      template.followUpPromoter
    );
  }

  // CATEGORY
  const categoryElement = page.elements?.find((element) => element.name === 'category');
  if (categoryElement) {
    applyCategoryElement(categoryElement, template);
  }

  // COMPLETED PAGE
  applyOutro(schema, template, ratingMaxValue);

  return ratingMaxValue;
};

export const getModifiedSurveySchema = ({ schema, template }: ModifiedSchemaArgs): SurveyJson => {
  schema.locale = schema.locale ?? getLocaleFromCountryName(template.language?.name);

  // find each page
  schema.pages?.forEach((page) => {
    applyPage(page, schema, template);
  });

  schema.completedHtml = applySurveyTextRules(schema.completedHtml) ?? undefined;
  schema.completedHtmlOnCondition?.forEach((condition) => {
    condition.html = applySurveyTextRules(condition.html) ?? '';
  });

  return schema;
};

const getSurveyAnswersSimple = (survey: any): Record<string, unknown> => {
  const surveyPlainData = survey.getPlainData();
  const surveyAnswersSimple = surveyPlainData.reduce(
    (acc: Record<string, unknown>, curr: Record<string, unknown>) => {
      const { name, value } = curr;
      acc[name as string] = { ...curr, value: value === undefined ? null : value };
      return acc;
    },
    {}
  );

  return surveyAnswersSimple;
};

export const getSurveyAnswers = (survey: any): SurveyAnswer[] => {
  const surveyPlainData = survey.getPlainData();
  return surveyPlainData.map((answer: Record<string, unknown>) => {
    return { ...answer, value: answer.value === undefined ? null : answer.value };
  });
};

const mapChosenCategory = (
  surveyAnswersSimple: Record<string, any>,
  template: Template
): Category | null => {
  if (!surveyAnswersSimple.category?.value) {
    return null;
  }

  const categoryFoundOnTemplate = template.categories?.find(
    (c) =>
      c.name === surveyAnswersSimple.category?.value ||
      c.name === surveyAnswersSimple.category?.displayValue
  );

  return {
    id: categoryFoundOnTemplate?.id,
    name: categoryFoundOnTemplate?.name ?? surveyAnswersSimple.category?.displayValue
  };
};

/* updates response with the newest survey data */
export function updateResponse(
  closedInformation: ClosedInformation | null,
  survey: any,
  surveyType: SurveyTypeValue,
  response: SurveyResponse,
  template: Template
) {
  if (closedInformation?.closed) {
    response.rating = closedInformation.previousRating; // if response is closed, the current rating is irrelevant
    response.closed = true; // set current response closed to true so that response does not re-open
  } else response.rating = survey.data.rating;
  const surveyAnswersSimple = getSurveyAnswersSimple(survey);
  // comment name is 'comment' for universal campaigns, 'userComment' for all others.

  const commentKey = surveyType === SurveyType.UNIVERSAL ? 'comment' : 'userComment';
  response.category = mapChosenCategory(surveyAnswersSimple, template);
  response[commentKey] =
    survey.data.nps_comment ||
    survey.data.satisfied_experience ||
    survey.data.passive_experience ||
    survey.data.disappointing_experience;
  response.allowedForPublicUse = survey.data.consent;
  response.anon = survey.data.anonymous;
  response.surveyAnswers = getSurveyAnswers(survey);
  response.surveyAnswersSimple = getSurveyAnswersSimple(survey);
}

/* creates response for universal link campaign */
export function createAnonymousResponse(survey: any, template: Template): Record<string, unknown> {
  const surveyAnswersSimple = getSurveyAnswersSimple(survey);
  return {
    Category: mapChosenCategory(surveyAnswersSimple, template),
    Email: '',
    FirstName: 'Anonymous',
    LastName: null,
    Rating: survey.data.rating,
    Comment:
      survey.data.nps_comment ||
      survey.data.satisfied_experience ||
      survey.data.passive_experience ||
      survey.data.disappointing_experience,
    Device: navigator.userAgent,
    SurveyAnswers: getSurveyAnswers(survey),
    SurveyAnswersSimple: surveyAnswersSimple,
    AllowedForPublicUse: survey.data.consent ?? false
  };
}

/* creates response for popup campaign */
export function createPopupResponse(survey: any, template: Template): Record<string, unknown> {
  const surveyAnswersSimple = getSurveyAnswersSimple(survey);
  return {
    rating: survey.data.rating,
    category: mapChosenCategory(surveyAnswersSimple, template),
    userComment:
      survey.data.nps_comment ||
      survey.data.satisfied_experience ||
      survey.data.passive_experience ||
      survey.data.disappointing_experience,
    device: navigator.userAgent,
    allowedForPublicUse: survey.data.consent ?? false,
    anon: survey.data.anonymous,
    surveyAnswers: getSurveyAnswers(survey),
    surveyAnswersSimple
  };
}

/* shows a page stating why survey is inaccessible. */
export function showSurveyInaccessiblePage(
  survey: any,
  type: InaccessibleTypeValue,
  template: Template
) {
  const name = getKeyByValue(SurveyInaccessibleType, type);
  survey.showNavigationButtons = 'none';
  let newPage = survey.pages.find((p: any) => {
    return p.name === name; // check if page is already in json
  });
  if (newPage === undefined) {
    // if not then add a simple html page showing the correct message.
    newPage = survey.addNewPage(name);
    // Add a FirstName question to the page
    const htmlElement = newPage.addNewQuestion('html');
    const language = template.language?.name ?? 'English';
    if (type === SurveyInaccessibleType.CAMPAIGN_CLOSED)
      htmlElement.html = campaignIsClosedHtml(template.campaignClosedText);
    else if (type === SurveyInaccessibleType.ALREADY_ANSWERED)
      htmlElement.html = surveyAlreadyAnsweredHtml(Languages.ALREADY_ANSWERED[language]);
    else if (type === SurveyInaccessibleType.UNSUBSCRIBED)
      htmlElement.html = unsubscribedHtml(template.unsubscribeText);
    else if (type === SurveyInaccessibleType.RESPONSE_LIMIT_REACHED)
      htmlElement.html = responseLimitReachedHtml(language);
    else htmlElement.html = unknownReasonHtml();
  }
  newPage.visible = true;
  survey.currentPage = newPage; // set current page to newpage.
}

/* sets css classes for styling the survey. */
export function setCssClasses(options: any) {
  const classes = options.cssClasses;
  classes.withFrame += ' contentBox';
}

/* experimental animation using 'velocity-animate' */
export function animate(animitionType: string, duration = 1000) {
  const element = document.getElementById('surveyElement');
  $(element).velocity(animitionType, { duration });
}

/*
Gets first found key that has to do with a specific value.
Also, sets it to lowercase.
Example: Status value 0 = success.
*/
function getKeyByValue(object: Record<string, number>, value: number): string {
  return (
    Object.keys(object)
      .find((key) => object[key] === value)
      ?.toLowerCase() ?? ''
  );
}

const campaignIsClosedHtml = (text?: string) =>
  applyHtml(
    text || '<h2>Campaign is closed</h2><p>This survey is no longer accepting responses.</p>'
  );

const responseLimitReachedHtml = (language: string) =>
  applyHtml(
    `<h2>${
      Languages.ACCEPTING_RESPONSES[language] ?? Languages.ACCEPTING_RESPONSES.English
    }</h2><p>${Languages.RESPONSE_NOT_SAVED[language] ?? Languages.RESPONSE_NOT_SAVED.English}</p>`
  );

const surveyAlreadyAnsweredHtml = (text?: string) =>
  applyHtml(
    text ||
      '<h2>Survey has already been answered</h2><p>This questionnaire has already been answered</p>'
  );

const unsubscribedHtml = (text?: string) =>
  applyHtml(
    text ||
      "<h2>You've unsubscribed</h2><p>You've unsubscribed and will no longer receive surveys from us</p>"
  );
const unknownReasonHtml = (text?: string) =>
  applyHtml(
    text ||
      '<h2>This survey is no longer accessible.</h2><p>It is no longer possible to respond to this survey</p>'
  );
const applyHtml = (text?: string) => {
  return `<div style="text-align: center;">
	<p>${applySurveyTextRules(text)}</p>
	</div>`;
};
